import React from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import documents from '../../../utils/docTypes';
import useMixPanel from 'hooks/useMixPanel';
import {valueof} from '../../../utils/types';
import {PATHS} from 'Routes';
import {useErrorModal} from 'utils/hooks';
import {useComputedReservationDetails} from 'context/computedReservationDetails';
import {
  IdentityLocationStateType,
  LocationStates,
} from 'views/VerifyModalScreen/VerifyModalScreen';
import {ConfirmIdentityTemplate} from 'components/ConfirmIdentity';
import {useTranslation} from 'react-i18next';
import {useMRZDetection} from '../../../hooks';
import {useIdentityVerification} from '../useIdentityVerification';

type BackSideDocumentIdentityProps = {
  documentType: valueof<typeof documents.types>;
  nationality: string;
};
function BackSideDocumentIdentity({
  documentType,
  nationality,
}: BackSideDocumentIdentityProps) {
  const history = useHistory();
  const {t} = useTranslation();
  const location = useLocation<IdentityLocationStateType>();
  const {displayError, ErrorModal} = useErrorModal();
  const reservationDetails = useComputedReservationDetails();
  const {isNeedCheckMrz} = useIdentityVerification({
    nationality,
    documentType,
  });
  const {IdVerificationDocument} = useMixPanel();
  const preloadedPhoto = location.state?.[LocationStates.backSideDocumentPhoto]?.picture;

  const {
    startMRZDetection,
    displayMrzError,
    status: statusMrzDetection,
    MrzErrorModal,
  } = useMRZDetection();

  const getPersistedState = (
    image: string,
    data?: Record<string, any>,
    isPassed?: boolean,
  ): IdentityLocationStateType => {
    return {
      ...location.state,
      [LocationStates.backSideDocumentPhoto]: {
        ...data,
        checkId: data?.id,
        picture: image,
      },
      [LocationStates.identityVerificationState]: {
        documentsPassed: isPassed,
        isIdentityVerificationFinished: typeof isPassed !== 'undefined',
      },
    };
  };

  const handleSuccessMrzDetection = (image: string, data: any) => {
    const state = getPersistedState(image, data, true);
    IdVerificationDocument('Success');
    return history.push(PATHS.verificationResult, {...state, isVerifiedOrRetake: true});
  };

  const handleErrorMrzDetection = (image: string) => {
    IdVerificationDocument('Error');
    if (reservationDetails.isVerifyOnlyDocumentOptional) {
      const state = getPersistedState(image, {}, false);
      return history.push(PATHS.verificationResult, {
        ...state,
        isVerifiedOrRetake: true,
      });
    }
    displayMrzError();
  };

  const handleMrzDetection = async (image: string, reservationId: string) => {
    try {
      await startMRZDetection({
        image,
        reservationId,
        onSuccess: handleSuccessMrzDetection,
        onError: () => handleErrorMrzDetection(image),
      });
    } catch (error) {
      IdVerificationDocument('Error');
      displayError(error);
    }
  };

  const sendScreenshot = (screenshot: string) => {
    const reservationId = reservationDetails?.reservation?.id;
    const needCheckMrz = reservationDetails.hasScanDocument && isNeedCheckMrz;

    if (needCheckMrz) {
      handleMrzDetection(screenshot, reservationId);
      return;
    }
    IdVerificationDocument('Success');
    history.push(PATHS.verificationSelfie, getPersistedState(screenshot));
  };

  // const enableScreenshotButton = () => {
  //   if (isMounted.current) {
  //     setIsCaptureButtonDisabled(false);
  //   }
  // };
  //
  //@ts-ignore
  // const isFileUpload = location?.state?.identityType === IdentityVerificationMethod.file;
  return (
    <>
      <ConfirmIdentityTemplate
        status={statusMrzDetection}
        onSubmit={sendScreenshot}
        detailsText={t('now_we_take_back_photo')}
        stateName={LocationStates.backSideDocumentPhoto}
        preloadedScreenshot={preloadedPhoto}
      />
      <ErrorModal />
      <MrzErrorModal />
    </>
  );
}

export {BackSideDocumentIdentity};
